import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

interface AdminUser {
  id: string;
  user_id: string;
  role: 'admin' | 'super_admin';
  created_at: string;
}

export function useAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check admin status
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle instead of single to handle no rows gracefully

      if (adminError && adminError.code !== 'PGRST116') {
        throw adminError;
      }

      if (adminData) {
        setIsAdmin(true);
        setIsSuperAdmin(adminData.role === 'super_admin');
        localStorage.setItem('isAdmin', 'true');
        localStorage.setItem('isSuperAdmin', (adminData.role === 'super_admin').toString());
      } else {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        localStorage.setItem('isAdmin', 'false');
        localStorage.setItem('isSuperAdmin', 'false');
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      setError(err instanceof Error ? err.message : 'Failed to check admin status');
      setIsAdmin(false);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const manageSubscription = useCallback(async (
    userId: string, 
    tier: string, 
    extendTrial?: boolean, 
    trialDays?: number
  ) => {
    try {
      setActionInProgress(true);
      if (!isAdmin) throw new Error('Unauthorized');

      // Map 'church' tier to 'premium', keep 'free_premium' as is
      const actualTier = tier === 'church' ? 'premium' : tier;

      logger.log('Managing subscription:', {
        userId,
        tier: actualTier,
        extendTrial,
        trialDays
      });

      // Make the RPC call to update the subscription
      const { error } = await supabase.rpc('admin_manage_subscription', {
        target_user_id: userId,
        new_tier: actualTier,
        extend_trial: extendTrial || false,
        trial_days: trialDays || 14
      });

      if (error) {
        console.error('RPC Error:', error);
        throw error;
      }

      logger.log('Subscription updated successfully');
      return true;
    } catch (err) {
      console.error('Error managing subscription:', err);
      throw err;
    } finally {
      setActionInProgress(false);
    }
  }, [isAdmin]);

  const deleteUser = useCallback(async (userId: string) => {
    try {
      setActionInProgress(true);
      if (!isSuperAdmin) throw new Error('Unauthorized');

      const { error } = await supabase.rpc('admin_delete_user', {
        target_user_id: userId
      });

      if (error) throw error;
    } catch (err) {
      console.error('Error deleting user:', err);
      throw err;
    } finally {
      setActionInProgress(false);
    }
  }, [isSuperAdmin]);

  const endTrial = useCallback(async (userId: string) => {
    try {
      setActionInProgress(true);
      if (!isAdmin) throw new Error('Unauthorized');

      const { error } = await supabase.rpc('admin_manage_subscription', {
        target_user_id: userId,
        new_tier: 'premium',
        extend_trial: true,
        trial_days: 0
      });

      if (error) throw error;
      return true;
    } catch (err) {
      console.error('Error ending trial:', err);
      throw err;
    } finally {
      setActionInProgress(false);
    }
  }, [isAdmin]);

  const revokeSubscription = useCallback(async (userId: string) => {
    try {
      setActionInProgress(true);
      if (!isSuperAdmin) throw new Error('Unauthorized');

      const { error } = await supabase.rpc('admin_revoke_subscription', {
        target_user_id: userId
      });

      if (error) {
        console.error('admin_revoke_subscription failed, falling back:', error);
        const { error: fallbackError } = await supabase.rpc('admin_manage_subscription', {
          target_user_id: userId,
          new_tier: 'premium',
          extend_trial: true,
          trial_days: 0
        });
        if (fallbackError) throw fallbackError;
      }
      return true;
    } catch (err) {
      console.error('Error revoking subscription:', err);
      throw err;
    } finally {
      setActionInProgress(false);
    }
  }, [isSuperAdmin]);

  const resetToTrial = useCallback(async (userId: string, accountCreatedAt: string) => {
    try {
      setActionInProgress(true);
      if (!isAdmin) throw new Error('Unauthorized');

      const createdDate = new Date(accountCreatedAt);
      const trialEndDate = new Date(createdDate);
      trialEndDate.setDate(trialEndDate.getDate() + 14);
      
      const now = new Date();
      const daysRemaining = Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

      const { error } = await supabase.rpc('admin_manage_subscription', {
        target_user_id: userId,
        new_tier: 'premium',
        extend_trial: true,
        trial_days: daysRemaining
      });

      if (error) throw error;
      return true;
    } catch (err) {
      console.error('Error resetting to trial:', err);
      throw err;
    } finally {
      setActionInProgress(false);
    }
  }, [isAdmin]);

  return {
    isAdmin,
    isSuperAdmin,
    loading,
    error,
    actionInProgress,
    manageSubscription,
    deleteUser,
    endTrial,
    revokeSubscription,
    resetToTrial,
    refreshStatus: checkAdminStatus
  };
}